import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import type { TrafficRow, CapacitySummary } from './rows';
import { MirrorTooltip, type MirrorSeries } from './MirrorTooltip';
import { ShowValues } from './ShowValues';
import { formatBits, formatLinkSpeed, formatPercent } from './format';
import { CHART_MARGIN, Y_AXIS_WIDTH, mirroredTicks } from './chart';

const BITS_SERIES: MirrorSeries[] = [
  { dataKey: 'bitsIn', label: 'into this port', color: 'var(--series-1)', format: formatBits },
  { dataKey: 'bitsOut', label: 'out of this port', color: 'var(--series-2)', format: formatBits },
];

const PERCENT_SERIES: MirrorSeries[] = [
  { dataKey: 'utilizationIn', label: 'into this port', color: 'var(--series-1)', format: formatPercent },
  { dataKey: 'utilizationOut', label: 'out of this port', color: 'var(--series-2)', format: formatPercent },
];

/**
 * Below this share of the line rate the capacity rail is not drawn.
 *
 * It is one axis, so putting a 1 Gbit/s rail on a port carrying 400 kbit/s
 * compresses the traffic into a hairline to show a number the utilization tile
 * already states exactly. The rail earns its ink when the reader is close
 * enough to it to care, and the caption names the speed either way.
 */
const RAIL_VISIBLE_ABOVE = 0.1;

/**
 * Throughput, inbound above the line and outbound below.
 *
 * One signed bits/sec axis whose ticks print magnitude — not a dual axis.
 * Direction rides the sign, which is the form every network engineer has read
 * for twenty years, makes asymmetry visible as shape, and frees hue entirely
 * for identity: `--series-1` means inbound and `--series-2` outbound across
 * this whole feature.
 *
 * Stacking would be wrong outright: a full-duplex port is not full at in+out,
 * it is full when either arm reaches its own rail.
 */
export function TrafficMirror({
  rows,
  start,
  end,
  syncId,
  capacity,
  caption,
  mode,
  boundOverride,
  directionLabels,
}: {
  rows: TrafficRow[];
  start: number;
  end: number;
  syncId: string;
  capacity: CapacitySummary;
  caption: string;
  /** `percent` is offered only where a denominator resolved; see `capacity.ts`. */
  mode: 'bits' | 'percent';
  /**
   * Force the axis to a given magnitude.
   *
   * The link panel locks both ends to one scale, because the point of showing
   * two ends side by side is that they can be compared by height. Two
   * independently-scaled mirrors put a 40 Mb/s port and a 4 Mb/s port at the
   * same apparent size, which is worse than not showing them together.
   */
  boundOverride?: number;
  /** What the two arms are, where they aren't a port's in and out (a wireless client's link). */
  directionLabels?: { in: string; out: string };
}) {
  const spansDays = end - start > 24 * 3600 * 1000;
  const percent = mode === 'percent';
  const series = useMemo(() => {
    const base = percent ? PERCENT_SERIES : BITS_SERIES;
    return directionLabels
      ? [{ ...base[0], label: directionLabels.in }, { ...base[1], label: directionLabels.out }]
      : base;
  }, [percent, directionLabels]);
  const formatValue = percent ? formatPercent : formatBits;

  const { bound: ownBound, showRail, peakIn, peakOut } = useMemo(() => {
    const keys = percent ? (['utilizationIn', 'utilizationOut'] as const) : (['bitsIn', 'bitsOut'] as const);
    const magnitudes = rows.flatMap((row) =>
      keys.flatMap((key) => {
        const value = row[key];
        return value === null ? [] : [Math.abs(value)];
      }),
    );
    const peak = magnitudes.length > 0 ? Math.max(...magnitudes) : 0;
    // In percent the axis IS the line rate, so it is pinned at 100 and the rail
    // is always the edge of the plot. Rescaling it to the data would make a
    // 3%-utilized port look identical to a saturated one.
    if (percent) {
      return {
        bound: 100,
        showRail: false,
        peakIn: maxOf(rows.map((row) => row.utilizationIn)),
        peakOut: maxOf(rows.map((row) => row.utilizationOut)),
      };
    }
    const rail = capacity.bits !== null && capacity.bits > 0 ? capacity.bits : null;
    const visible = rail !== null && peak >= rail * RAIL_VISIBLE_ABOVE;
    return {
      bound: Math.max(visible ? rail * 1.08 : 0, peak * 1.15, 1),
      showRail: visible,
      peakIn: maxOf(rows.map((row) => row.bitsIn)),
      peakOut: maxOf(rows.map((row) => row.bitsOut)),
    };
  }, [rows, capacity.bits, percent]);
  const bound = boundOverride !== undefined && boundOverride > 0 && !percent ? boundOverride : ownBound;

  return (
    <div>
      <h3 className="text-xs font-semibold mb-1">{percent ? 'Utilization' : 'Throughput'}</h3>
      <p className="text-xs text-muted-foreground mb-3">{caption}</p>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={rows} syncId={syncId} syncMethod="value" margin={CHART_MARGIN}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={[start, end]}
            tickFormatter={(ts: number) => format(new Date(ts), spansDays ? 'MMM d HH:mm' : 'HH:mm')}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            minTickGap={48}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            width={Y_AXIS_WIDTH}
            domain={[-bound, bound]}
            ticks={mirroredTicks(bound)}
            allowDataOverflow
            tickFormatter={(v: number) => (v === 0 ? '0' : formatValue(Math.abs(v)))}
          />
          <ReferenceLine y={0} stroke="var(--border)" strokeWidth={1} />

          <Tooltip
            cursor={{ stroke: 'var(--fg-subtle)', strokeWidth: 1 }}
            content={<MirrorTooltip series={series} spansDays={spansDays} />}
          />

          {series.map((spec) => (
            <Area
              key={spec.dataKey}
              type="monotone"
              dataKey={spec.dataKey}
              stroke={spec.color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={spec.color}
              fillOpacity={0.1}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
              // Deliberately NOT connectNulls, matching the incident chart. A
              // null here is a bucket the store knows nothing about — a missed
              // poll, or an interval whose only reading was a counter reset the
              // query filtered out. Bridging it draws a rate nobody observed.
              isAnimationActive={false}
            />
          ))}

          {showRail
            ? (['capacity', 'capacityMirror'] as const).map((key) => (
                <Line
                  key={key}
                  type="stepAfter"
                  dataKey={key}
                  stroke="var(--fg-subtle)"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))
            : null}
        </ComposedChart>
      </ResponsiveContainer>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {series.map((spec, index) => (
          <li key={spec.dataKey} className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block w-3 h-0.5 rounded-full shrink-0"
              style={{ backgroundColor: spec.color }}
              aria-hidden="true"
            />
            <span className="text-foreground">{spec.label}</span>
            <span className="text-muted-foreground">
              {(index === 0 ? peakIn : peakOut) === null
                ? 'not reported'
                : `peak ${formatValue((index === 0 ? peakIn : peakOut) ?? 0)}`}
            </span>
          </li>
        ))}
        {showRail && capacity.bits !== null ? (
          <li className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block w-3 border-t border-dashed shrink-0"
              style={{ borderColor: 'var(--fg-subtle)' }}
              aria-hidden="true"
            />
            <span className="text-muted-foreground">line rate {formatLinkSpeed(capacity.bits)}</span>
          </li>
        ) : null}
      </ul>

      <ShowValues
        columns={[
          { key: 'ts', label: 'Time' },
          { key: 'in', label: 'Into port' },
          { key: 'out', label: 'Out of port' },
        ]}
        rows={rows.map((row) => {
          const inbound = percent ? row.utilizationIn : row.bitsIn;
          const outbound = percent ? row.utilizationOut : row.bitsOut;
          return {
            ts: format(new Date(row.ts), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss'),
            in: inbound === null ? '—' : formatValue(Math.abs(inbound)),
            out: outbound === null ? '—' : formatValue(Math.abs(outbound)),
          };
        })}
      />
    </div>
  );
}

function maxOf(values: (number | null)[]): number | null {
  const present = values.flatMap((v) => (v === null ? [] : [Math.abs(v)]));
  return present.length > 0 ? Math.max(...present) : null;
}
